import {useQuery, keepPreviousData, useQueries} from "@tanstack/react-query";
import {Device} from "../types/device";
import {DeviceFilters} from "../types/filters";
import {buildQueryParams} from "./buildQuery";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Update: Now accepts chunk and returns an object with total_count
async function fetchDevices(filters: DeviceFilters, chunk: number): Promise<{
    devices: Device[],
    total_count: number
}> {
    const query = buildQueryParams(filters);
    // Add chunk to the query string
    const res = await fetch(`${API_BASE_URL}/api/phones?${query}&chunk=${chunk}`);
    if (!res.ok) throw new Error("Failed to fetch");

    return res.json();
}

async function fetchSuggestions(query: string): Promise<{ id: number; text: string }[]> {
    if (query.length < 3) return [];

    const res = await fetch(`${API_BASE_URL}/api/phones/suggestions?q=${encodeURIComponent(query)}`);
    if (!res.ok) throw new Error("Failed to fetch suggestions");

    return res.json();
}

async function fetchRelatedDevices(id: string): Promise<Device[]> {
    const res = await fetch(`${API_BASE_URL}/api/phones/related/${id}`);
    if (!res.ok) throw new Error("Failed to fetch related devices");
    return res.json();
}

// Update: Backend now returns { count: number } object
async function countFilteredDevices(filters: DeviceFilters): Promise<{ count: number, available_brands: string[] }> {
    const query = buildQueryParams(filters);
    const res = await fetch(`${API_BASE_URL}/api/phones/count?${query}`);
    if (!res.ok) throw new Error("Failed to fetch");
    return res.json();
}

async function fetchDevice(id: string): Promise<Device> {
    const res = await fetch(`${API_BASE_URL}/api/phones/phone/${id}`);
    if (!res.ok) throw new Error("Failed to fetch");
    return res.json();
}

// --- Hooks ---

export function useDevices(filters: DeviceFilters, chunk: number = 0) {
    return useQuery({
        queryKey: ["devices", filters, chunk],
        queryFn: () => fetchDevices(filters, chunk),
        staleTime: 1000 * 60 * 60,
        placeholderData: keepPreviousData, // This is what causes the "blink"
    });
}

export function useSuggestions(query: string) {
    return useQuery({
        queryKey: ["suggestions", query],
        queryFn: () => fetchSuggestions(query),
        enabled: query.length >= 2,
        staleTime: 1000 * 60 * 5,
        placeholderData: keepPreviousData,
    });
}

export function useFilteredDevicesCount(filters: DeviceFilters) {
    return useQuery({
        queryKey: ["filteredDevicesCount", filters],
        queryFn: () => countFilteredDevices(filters),
        // Important: Keep the last known count while user is toggling filters
        placeholderData: keepPreviousData,
    });
}

export function useDevice(id: string) {
    return useQuery({
        queryKey: ["device", id],
        queryFn: () => fetchDevice(id),
    });
}

export function useAllBrands() {
    return useQuery({
        queryKey: ["all-brands"],
        queryFn: async () => {
            const res = await fetch(`${API_BASE_URL}/api/phones/brands`); // You may need to add this endpoint to Rails
            if (!res.ok) throw new Error("Failed to fetch brands");
            return res.json(); // Expected: string[]
        },
        staleTime: 1000 * 60 * 60 * 24, // Cache for 24 hours (brands don't change often)
    });
}

export function useComparison(ids: number[]) {
    const results = useQueries({
        queries: ids.map((id) => ({
            queryKey: ["device", id.toString()], // Reuse the same key as useDevice
            queryFn: () => fetchDevice(id.toString()),
            staleTime: 1000 * 60 * 10,
        })),
    });

    // Derived values for the UI
    const devices = results
        .map((r) => r.data)
        .filter((d): d is Device => !!d);

    const isLoading = results.some((r) => r.isLoading);
    const isError = results.some((r) => r.isError);

    return { data: devices, isLoading, isError };
}

export function useRelatedDevices(id: string | undefined) {
    return useQuery({
        queryKey: ["related-devices", id],
        queryFn: () => fetchRelatedDevices(id!),
        enabled: !!id, // Only run if id is provided
        staleTime: 1000 * 60 * 60, // 1 hour cache
    });
}